/**
 * CartContext — manages shopping cart state per business.
 * Session is tracked server-side via x-cart-session header.
 */
import { createContext, useContext, useState, useCallback, useRef } from "react";
import {
  fetchCart,
  addToCart as apiAddToCart,
  updateCartItem as apiUpdateCartItem,
  removeCartItem as apiRemoveCartItem,
  clearCart as apiClearCart,
  checkout as apiCheckout,
} from "../services/cartService";
import useToast from "../components/common/useToast";

const CartContext = createContext(null);

/**
 * Cart state shape per business:
 * { items: [], total: 0, count: 0, businessName: "" }
 */

export const CartProvider = ({ children }) => {
  // keyed by businessSlug
  const [carts, setCarts] = useState({});
  const [isCartOpen, setCartOpen] = useState(false);
  const [activeBusinessSlug, setActiveBusinessSlug] = useState(null);
  const [loadingItems, setLoadingItems] = useState({});
  const toast = useToast();
  // Prevent duplicate simultaneous fetchCart calls
  const fetchingRef = useRef({});

  const getCart = useCallback(
    (slug) => carts[slug] || { items: [], total: 0, count: 0, businessName: "" },
    [carts]
  );

  const setItemLoading = useCallback((key, val) => {
    setLoadingItems((prev) => ({ ...prev, [key]: val }));
  }, []);

  /** Sync local state from server response */
  const syncFromServer = useCallback((slug, data) => {
    if (!data) return;
    const items = Array.isArray(data.items) ? data.items : [];
    const total = items.reduce((sum, it) => sum + (Number(it.unitPrice ?? it.price ?? 0) * Number(it.qty ?? it.quantity ?? 1)), 0);
    const count = items.reduce((sum, it) => sum + Number(it.qty ?? it.quantity ?? 1), 0);
    setCarts((prev) => ({
      ...prev,
      [slug]: {
        items,
        total,
        count,
        businessName: data.businessName || prev[slug]?.businessName || "",
      },
    }));
  }, []);

  /** Load cart from server (idempotent) */
  const loadCart = useCallback(
    async (businessSlug) => {
      if (fetchingRef.current[businessSlug]) return;
      fetchingRef.current[businessSlug] = true;
      try {
        const data = await fetchCart(businessSlug);
        syncFromServer(businessSlug, data);
      } catch (_) {
        // Not blocking — cart may not exist yet
      } finally {
        fetchingRef.current[businessSlug] = false;
      }
    },
    [syncFromServer]
  );

  const addItem = useCallback(
    async (businessSlug, productId, qty = 1, productName = "") => {
      const loadKey = `add-${businessSlug}-${productId}`;
      setItemLoading(loadKey, true);
      try {
        const data = await apiAddToCart(businessSlug, productId, qty);
        syncFromServer(businessSlug, data);
        setActiveBusinessSlug(businessSlug);
        setCartOpen(true);
        toast.success(
          productName
            ? `${productName} added to cart`
            : "Item added to cart"
        );
      } catch (err) {
        toast.error(err?.message || "Failed to add item to cart");
      } finally {
        setItemLoading(loadKey, false);
      }
    },
    [syncFromServer, setItemLoading, toast]
  );

  const updateItem = useCallback(
    async (businessSlug, itemId, qty) => {
      const loadKey = `update-${businessSlug}-${itemId}`;
      setItemLoading(loadKey, true);
      try {
        const data = await apiUpdateCartItem(businessSlug, itemId, qty);
        syncFromServer(businessSlug, data);
      } catch (err) {
        toast.error(err?.message || "Failed to update item");
      } finally {
        setItemLoading(loadKey, false);
      }
    },
    [syncFromServer, setItemLoading, toast]
  );

  const removeItem = useCallback(
    async (businessSlug, itemId) => {
      const loadKey = `remove-${businessSlug}-${itemId}`;
      setItemLoading(loadKey, true);
      try {
        const data = await apiRemoveCartItem(businessSlug, itemId);
        syncFromServer(businessSlug, data);
      } catch (err) {
        toast.error(err?.message || "Failed to remove item");
      } finally {
        setItemLoading(loadKey, false);
      }
    },
    [syncFromServer, setItemLoading, toast]
  );

  const clearCartForBusiness = useCallback(
    async (businessSlug) => {
      try {
        await apiClearCart(businessSlug);
        setCarts((prev) => ({
          ...prev,
          [businessSlug]: { items: [], total: 0, count: 0, businessName: prev[businessSlug]?.businessName || "" },
        }));
        setCartOpen(false);
      } catch (err) {
        toast.error(err?.message || "Failed to clear cart");
      }
    },
    [toast]
  );

  const doCheckout = useCallback(
    async (businessSlug, checkoutData = {}) => {
      try {
        const result = await apiCheckout(businessSlug, checkoutData);
        // Clear local cart on success
        setCarts((prev) => ({
          ...prev,
          [businessSlug]: { items: [], total: 0, count: 0, businessName: prev[businessSlug]?.businessName || "" },
        }));
        setCartOpen(false);
        toast.success("Order placed successfully!");
        return result;
      } catch (err) {
        toast.error(err?.message || "Checkout failed");
        throw err;
      }
    },
    [toast]
  );

  const activeCart = activeBusinessSlug ? getCart(activeBusinessSlug) : { items: [], total: 0, count: 0 };

  const value = {
    // Per-business cart access
    getCart,
    loadCart,
    // Active (currently viewed) cart
    cartItems: activeCart.items,
    cartCount: activeCart.count,
    cartTotal: activeCart.total,
    // Actions
    addItem,
    updateItem,
    removeItem,
    clearCart: clearCartForBusiness,
    checkout: doCheckout,
    // UI state
    isCartOpen,
    setCartOpen,
    activeBusinessSlug,
    setActiveBusinessSlug,
    loadingItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
